// Number/date/countdown formatting. toFa() gives Persian digits for fa
// and plain Latin for every other language.
import { useState, useEffect } from "react";
import { moduleLang, tt } from "../i18n.jsx";

// Persian digits for fa; plain Latin for en (kept in sync via moduleLang()).
export function toFa(n) { return moduleLang() === "fa" ? String(n).replace(/\d/g, d => "۰۱۲۳۴۵۶۷۸۹"[d]) : String(n); }
export function pad(n)  { return String(n).padStart(2,"0"); }
export function fmtTime(h, m) { return `${toFa(pad(h))}:${toFa(pad(m))}`; }

export function getNextOccurrence(dayIdx, hour, minute) {
  // Get next Tehran date for given (dayIdx, hour, minute)
  // Since we can't easily compute Tehran timezone in browser without libs,
  // we approximate using UTC+3:30 offset
  const tehranOffset = 3.5 * 60 * 60 * 1000;
  const nowUtc = Date.now();
  const nowTehran = new Date(nowUtc + tehranOffset);

  const jsDay = [6, 0, 1, 2, 3, 4, 5][dayIdx]; // 0=Sat→6 in JS
  let candidate = new Date(nowTehran);
  candidate.setHours(hour, minute, 0, 0);

  const currentJsDay = nowTehran.getDay();
  let daysAhead = (jsDay - currentJsDay + 7) % 7;
  if (daysAhead === 0 && candidate <= nowTehran) daysAhead = 7;
  candidate.setDate(candidate.getDate() + daysAhead);

  // Convert back to UTC
  return new Date(candidate.getTime() - tehranOffset);
}

export function useCountdown(schedule) {
  const [msLeft, setMsLeft] = useState(null);
  const [nextSlot, setNextSlot] = useState(null);

  useEffect(() => {
    if (!schedule) return;
    function update() {
      const t1 = getNextOccurrence(schedule.slot1.day, schedule.slot1.hour, schedule.slot1.minute);
      const t2 = getNextOccurrence(schedule.slot2.day, schedule.slot2.hour, schedule.slot2.minute);
      const next = t1 < t2 ? t1 : t2;
      const slot = t1 < t2 ? schedule.slot1 : schedule.slot2;
      setNextSlot(slot);
      setMsLeft(next - Date.now());
    }
    update();
    const id = setInterval(update, 30000); // update every 30s
    return () => clearInterval(id);
  }, [schedule]);

  return { msLeft, nextSlot };
}

export function formatCountdown(ms) {
  if (ms === null) return "";
  if (ms <= 0)        return { text: tt("قصه آماده است! 🎉"), pulse: true };
  const mins  = Math.floor(ms / 60000);
  if (mins < 10)      return { text: tt("کمتر از ۱۰ دقیقه"), pulse: true };
  if (mins < 60)      return { text: tt("کمتر از یک ساعت"), pulse: false };
  const days     = Math.floor(mins / 1440);
  const hours    = Math.floor((mins % 1440) / 60);
  const remMins  = mins % 60;
  if (moduleLang() !== "fa") {
    const parts = [];
    if (days  > 0) parts.push(`${days} ${days === 1 ? "day" : "days"}`);
    if (hours > 0) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
    if (remMins > 0 && days === 0) parts.push(`${remMins} ${remMins === 1 ? "minute" : "minutes"}`);
    return { text: `${parts.join(", ")} left`, pulse: false };
  }
  // Build "۱ روز و ۳ ساعت و ۴ دقیقه دیگر", omitting any zero part.
  const parts = [];
  if (days  > 0) parts.push(`${toFa(days)} روز`);
  if (hours > 0) parts.push(`${toFa(hours)} ساعت`);
  if (remMins > 0 && days === 0) parts.push(`${toFa(remMins)} دقیقه`); // drop minutes once we're days out
  const text = `${parts.join(" و ")} دیگر`;
  return { text, pulse: false };
}

// ── Helpers ───────────────────────────────────────────────────────────────────
export function fmt(n) { return n.toLocaleString(moduleLang() === "fa" ? "fa-IR" : "en-US"); }
